// shared imports
import messageCatalog, {__} from '~/shared/src/utilities/messageCatalog';


type Placeholders = {
    [ placeholder : string ] : string;
};

export type ToJsonResult = {
    type : 'localizable';
    name : string;
    doNotLocalize : boolean;
    message? : string;
    monoSpaceMessage? : boolean;
    doNotLocalizeMessage? : boolean;
    number? : number;
    placeholders? : Placeholders;
    severity? : 'error' | 'warning';
    position? : string | { line : number; column : number };
    errors? : never;
};

class LocalizableError extends Error {
    readonly type = 'localizable';

    readonly name : string;
    readonly placeholders : Placeholders;

    doNotLocalize : boolean = false;
    doNotLocalizeMessage : boolean = false;

    // the cause (the re-thrown error) of this error
    private _causedBy : any;

    protected _language : string | undefined;
    get language() {
        return this._language;
    }

    set language( language : string | undefined ) {
        this._language = language;
    }

    get nameLocalized() {
        let retVal : string;

        if ( !this.doNotLocalize ) {
            retVal = `${__( this.name, this.placeholders )}`;
        }
        else {
            retVal = this.name;
        }

        return retVal;
    }

    get messageLocalized() {
        let retVal : string | undefined;

        if ( this.message ) {
            if ( !this.doNotLocalize && !this.doNotLocalizeMessage ) {
                retVal = `${__( this.message, this.placeholders )}`;
            }
            else {
                retVal = this.message;
            }
        }

        return retVal;
    }

    constructor( errorMessageKey : string, placeholders : Placeholders = {}, errorStack? : string, errorStackTraceLength : number = 1, number? : number ) {
        super( errorMessageKey );

        this.name = errorMessageKey;
        this.message = '';
        this.placeholders = placeholders;

        if ( !errorStack ) {
            errorStack = this.stack;
        }

        if ( errorStack ) {
            this.position = '';

            const errorStackLines = errorStack.split( '\n' );

            for ( let i = 1; i < errorStackTraceLength + 1; i++ ) {
                let currentErrorStackLine = errorStackLines[ i ] ?? '';

                if ( currentErrorStackLine ) {
                    if ( currentErrorStackLine.indexOf( '(' ) !== -1 ) {
                        currentErrorStackLine = currentErrorStackLine.replace( /^.* (.*) \(.*:(.*):(\d*):\d*\)$/, 'at $2 ($1), line: $3' );
                    }
                    else {
                        currentErrorStackLine = currentErrorStackLine.replace( /^.* \/?.*:(.*):(\d*):\d*$/, 'at $1, line: $2' );
                    }

                    this.position += ( i > 0 ? '\n' : '' ) + currentErrorStackLine.trim();
                }
            }
        }

        if ( number ) {
            this.number = number;
        }
    }

    causedBy( error? : any ) {
        if ( error === undefined ) {
            return this._causedBy;
        }
        else {
            this._causedBy = error;
            return this;
        }
    }

    toJSON() : ToJsonResult {
        const retVal = {} as ToJsonResult;

        retVal.name = this.name;

        if ( this.message && this.message !== this.name ) {
            retVal.message = this.message;

            if ( this.monoSpaceMessage ) {
                retVal.monoSpaceMessage = this.monoSpaceMessage;
            }

            if ( this.doNotLocalizeMessage ) {
                retVal.doNotLocalizeMessage = this.doNotLocalizeMessage;
            }
        }

        if ( this.number !== undefined ) {
            retVal.number = this.number;
        }

        if ( this.position !== undefined ) {
            retVal.position = this.position;
        }

        if ( this.severity ) {
            retVal.severity = this.severity;
        }

        if ( Object.keys( this.placeholders ).length ) {
            retVal.placeholders = this.placeholders;
        }

        if ( this.doNotLocalize ) {
            retVal.doNotLocalize = this.doNotLocalize;
        }

        retVal.type = this.type;

        return retVal;
    }

    static fromJSON( data : ToJsonResult ) : LocalizableError {
        const retVal = new LocalizableError( data.name, data.placeholders );

        if ( data.message ) {
            retVal.message = data.message;

            if ( data.monoSpaceMessage ) {
                retVal.monoSpaceMessage = data.monoSpaceMessage;
            }

            if ( data.doNotLocalizeMessage ) {
                retVal.doNotLocalizeMessage = data.doNotLocalizeMessage;
            }
        }

        if ( data.number !== undefined ) {
            retVal.number = data.number;
        }

        if ( data.position !== undefined ) {
            retVal.position = data.position;
        }
        else {
            retVal.position = undefined;
        }

        if ( data.severity ) {
            retVal.severity = data.severity;
        }

        if ( data.doNotLocalize ) {
            retVal.doNotLocalize = data.doNotLocalize;
        }

        return retVal;
    }

    toString( withPosition : boolean = true ) {
        const currentLanguage = messageCatalog.language;

        let retVal = '';

        messageCatalog.language = this._language ?? global.user?.language ?? 'EN';

        if ( !this.doNotLocalize ) {
            retVal = __( this.name, this.placeholders );
        }
        else {
            retVal = this.name;
        }
        if ( retVal === this.name && Object.keys( this.placeholders ).length !== 0 ) {
            retVal += '\nplaceholders: ' + JSON.stringify( this.placeholders );
        }

        if ( this.message && this.message !== this.name ) {
            let message;
            if ( !this.doNotLocalize || !this.doNotLocalizeMessage ) {
                message = __( this.message, this.placeholders );
            }
            else {
                message = this.message;
            }
            retVal += `\n${message}`;
            if ( message === this.message && Object.keys( this.placeholders ).length !== 0 ) {
                retVal += '\nplaceholders: ' + JSON.stringify( this.placeholders );
            }
        }

        if ( withPosition && this.position ) {
            if ( typeof this.position === 'string' ) {
                retVal += `\n${this.position.indent( 2 )}`;
            }
            else {
                retVal += '\n' + `line:   ${this.position.line}`.indent( 2 );
                retVal += '\n' + `column: ${this.position.column}`.indent( 2 );
            }
        }

        messageCatalog.language = currentLanguage;

        return retVal;
    }

    toHTML() {
        const currentLanguage = messageCatalog.language;

        let retVal = '';

        messageCatalog.language = this._language ?? global.user?.language ?? 'EN';

        retVal += '<div class="yn-error-subtitle">';
        if ( !this.doNotLocalize ) {
            retVal += `${__( this.name, this.placeholders )}`;
        }
        else {
            retVal += this.name;
        }
        retVal += '</div>';

        if ( this.message && this.message !== this.name ) {
            if ( this.monoSpaceMessage ) {
                retVal += '<div class="yn-error-message-monospace">';
            }
            else {
                retVal += '<div class="yn-error-message">';
            }

            let message;
            if ( !this.doNotLocalize || !this.doNotLocalizeMessage ) {
                message = __( this.message, this.placeholders );
            }
            else {
                message = this.message;
            }

            message = message.replace( /</g, '&lt;' )
                             .replace( />/g, '&gt;' )
                             .replace( /\r?\n/g, '<br/>' );

            if ( this.monoSpaceMessage ) {
                message = message.replace( / /g, '&nbsp;' )
                                 .replace( /\^/g, '<div class="yn-error-position-marker fas fa-arrow-up"></div>' );
            }

            retVal += message;
            retVal += '</div>';
        }

        if ( this.position ) {
            retVal += '<div class="yn-error-position">';
            if ( typeof this.position === 'string' ) {
                retVal += this.position.replace( /\r?\n/g, '<br/>' );
            }
            else {
                retVal += `line:   ${this.position.line}`;
                retVal += `<br/>column: ${this.position.column}`;
            }
            retVal += '</div>';
        }

        messageCatalog.language = currentLanguage;

        return retVal;
    }
}

export default LocalizableError;
